import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { PageShell, Card } from "./page-shell";
import { Toc, slugifyHeading, type TocItem } from "./toc";
import { Faq } from "./faq";
import { SourceList } from "./source-list";
import { ShareCard } from "./share-card";
import { InlineLinks } from "./checklist";
import { getGuide, isRelatedPath, RELATED_PAGES, type Guide } from "@/lib/guides";
import type { Disease } from "@/lib/diseases";
import { freshness, parseReviewDate } from "@/lib/freshness";
import { cn } from "@/lib/utils";

/*
 * The reading kit. Guides, articles, species, diseases and the reference pages are
 * long-form, so they share one set of parts: a section with a rule over its headline,
 * running text at a readable measure, lists with the tick's spot as the bullet or a
 * hairline between items, numbered steps set in the headline face, and a contents list
 * in the margin. Before V5 every list item was its own bordered tile.
 */

/** Section headline. Newsreader at its book weight, never bold. */
export const H2 = "font-display text-[1.75rem] font-medium leading-tight text-foreground";

/** Running text: a size and leading for reading, held to a 66ch measure. */
export const PROSE = "measure text-[1.0625rem] leading-[1.7] text-foreground/90";

/** The kind of illness, as a short label. "Other" says nothing, so it is left blank. */
export const DISEASE_CATEGORY_LABEL: Record<Disease["category"], string> = {
  bacterial: "Bacterial",
  viral: "Viral",
  parasitic: "Parasitic",
  allergic: "Allergic",
  other: "",
};

/** Run-in lead for list items written "Adult female: reddish-brown body ...". */
function splitLead(text: string): [string, string] | null {
  const i = text.indexOf(": ");
  if (i <= 0 || i > 28) return null;
  const lead = text.slice(0, i);
  if (/[.;]/.test(lead)) return null;
  return [`${lead}:`, text.slice(i + 2)];
}

function WithLead({ text }: { text: string }) {
  const parts = splitLead(text);
  if (!parts) return <>{text}</>;
  return (
    <>
      <span className="font-semibold text-foreground">{parts[0]}</span> {parts[1]}
    </>
  );
}

/** A ruled section with an anchorable headline. */
export function Section({
  id,
  title,
  children,
  className,
}: {
  id?: string;
  title: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <h2 id={id} className={H2}>
        {title}
      </h2>
      <div className="mt-4 space-y-5">{children}</div>
    </Card>
  );
}

export function Prose({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn(PROSE, className)}>{children}</p>;
}

/** One item of a dot list, for when an item needs markup (bold run-ins, links). */
export function DotItem({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-3 break-inside-avoid">
      <span
        aria-hidden
        className="mt-[0.68em] h-1.5 w-1.5 shrink-0 rounded-full bg-rust [print-color-adjust:exact]"
      />
      <span className="min-w-0">{children}</span>
    </li>
  );
}

/** A plain list with the tick's spot as the bullet. `columns` sets short items in two. */
export function DotList({
  items,
  children,
  columns = false,
  className,
}: {
  items?: readonly string[];
  children?: ReactNode;
  columns?: boolean;
  className?: string;
}) {
  return (
    <ul
      className={cn(
        "measure text-[1.0625rem] leading-relaxed text-foreground/90",
        columns ? "sm:max-w-none sm:columns-2 sm:gap-10 [&>li]:mb-2" : "space-y-2",
        className,
      )}
    >
      {items?.map((t) => (
        <DotItem key={t}>
          <WithLead text={t} />
        </DotItem>
      ))}
      {children}
    </ul>
  );
}

/** Items with a hairline between them, for longer entries that each stand alone. */
export function RuledList({ items, className }: { items: readonly string[]; className?: string }) {
  return (
    <ul
      className={cn(
        "measure divide-y divide-border border-y border-border text-[1.0625rem] leading-relaxed text-foreground/90",
        className,
      )}
    >
      {items.map((t) => (
        <li key={t} className="py-3">
          <WithLead text={t} />
        </li>
      ))}
    </ul>
  );
}

/** Numbered steps: the number in the headline face, a hairline between each. */
export function Steps({
  items,
  className,
}: {
  items: ReadonlyArray<string | { title: string; text: string }>;
  className?: string;
}) {
  return (
    <ol className={cn("divide-y divide-border border-y border-border", className)}>
      {items.map((it, i) => {
        const key = typeof it === "string" ? it : it.title;
        return (
          <li key={key} className="grid grid-cols-[2.25rem_1fr] gap-x-2 py-3.5">
            <span aria-hidden className="tabular font-display text-xl leading-7 text-rust-ink">
              {i + 1}
            </span>
            {typeof it === "string" ? (
              <span className="text-[1.0625rem] leading-7 text-foreground/90">{it}</span>
            ) : (
              <span className="text-[1.0625rem] leading-7">
                <span className="block font-semibold text-foreground">{it.title}</span>
                <span className="mt-0.5 block text-[0.9375rem] leading-relaxed text-muted-foreground">
                  {it.text}
                </span>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** A small label-caps heading inside a section, over a paragraph or list. */
export function Subhead({ children }: { children: ReactNode }) {
  return <h3 className="label-caps mb-2 text-muted-foreground">{children}</h3>;
}

export function formatReviewDate(iso: string): string {
  const d = parseReviewDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * "Last reviewed June 1, 2026", in small type under the standfirst. The page end and the
 * footer carry the not-medical-advice note, so this line no longer repeats it. The age
 * is added after mount, once it matters (see ReviewedLine for why not during SSR).
 */
export function ReviewedDate({
  iso,
  label = "Last reviewed",
  extra,
}: {
  iso: string;
  label?: string;
  extra?: ReactNode;
}) {
  const [age, setAge] = useState<string | null>(null);
  useEffect(() => {
    const f = freshness(iso);
    setAge(f.stale ? f.age : null);
  }, [iso]);
  return (
    <p className="tabular -mt-6 text-xs text-muted-foreground sm:-mt-9">
      {label} <time dateTime={iso}>{formatReviewDate(iso)}</time>
      {age ? ` (${age})` : ""}
      {extra}
    </p>
  );
}

/**
 * The reading column, with the contents list in the right-hand margin on wide screens.
 * Without a contents list it is the same column, so every reading page shares a width.
 */
export function ArticleBody({ toc, children }: { toc?: TocItem[]; children: ReactNode }) {
  if (!toc || toc.length < 2) {
    return <div className="max-w-3xl space-y-12">{children}</div>;
  }
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_12.5rem] lg:gap-x-16">
      <aside className="mb-12 lg:col-start-2 lg:row-start-1 lg:mb-0">
        <Toc items={toc} className="lg:sticky lg:top-24" />
      </aside>
      <div className="min-w-0 space-y-12 lg:col-start-1 lg:row-start-1">{children}</div>
    </div>
  );
}

/**
 * A numbered clause, for the policy pages: the number in the headline face and rust, the
 * text at a reading measure. `titles` is the page's list of clause names, so the contents
 * list and the headings cannot drift apart.
 */
export function Clause({
  n,
  titles,
  children,
}: {
  n: number;
  titles: readonly string[];
  children: ReactNode;
}) {
  return (
    <Card>
      <h2
        id={`clause-${n}`}
        className="font-display text-2xl font-medium leading-tight text-foreground"
      >
        <span className="tabular mr-3 text-rust-ink">{n}</span>
        {titles[n - 1]}
      </h2>
      <div className="measure mt-3 space-y-3 text-base leading-[1.7] text-foreground/85 [&_strong]:font-semibold [&_strong]:text-foreground">
        {children}
      </div>
    </Card>
  );
}

export function clauseToc(titles: readonly string[]): TocItem[] {
  return titles.map((t, i) => ({ id: `clause-${i + 1}`, label: t }));
}

/** "Effective July 20, 2026", in the same quiet place as a review date. */
export function EffectiveDate({ date }: { date: string }) {
  return <p className="tabular -mt-6 text-xs text-muted-foreground sm:-mt-9">Effective {date}</p>;
}

/** A label-caps heading over a row of links separated by middots. */
export function MoreLinks({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <nav aria-labelledby={id}>
      <h2 id={id} className="label-caps text-muted-foreground">
        {title}
      </h2>
      <InlineLinks className="mt-3">{children}</InlineLinks>
    </nav>
  );
}

/** The closing note every reading page ends on, in the margin's voice. */
export function EndNote({ children }: { children: ReactNode }) {
  return <p className="measure text-xs leading-relaxed text-muted-foreground">{children}</p>;
}

/** "Read our sources and disclaimer." */
export function SourcesAndDisclaimer() {
  return (
    <>
      Read our{" "}
      <Link to="/sources" className="link">
        sources
      </Link>{" "}
      and{" "}
      <Link to="/disclaimer" className="link">
        disclaimer
      </Link>
      .
    </>
  );
}

const RELATED_CLASS =
  "block py-3 font-display text-lg leading-snug text-foreground transition-colors hover:text-primary";

/** One line of "Related reading": another guide by slug, or a site page by path. */
function RelatedLink({ target, children }: { target: string; children: ReactNode }) {
  if (target.startsWith("/diseases/")) {
    return (
      <Link
        to="/diseases/$disease"
        params={{ disease: target.slice("/diseases/".length) }}
        className={RELATED_CLASS}
      >
        {children}
      </Link>
    );
  }
  if (
    target === "/alpha-gal" ||
    target === "/tick-identification" ||
    target === "/tick-bite-guide"
  ) {
    return (
      <Link to={target} className={RELATED_CLASS}>
        {children}
      </Link>
    );
  }
  return (
    <Link to="/$guide" params={{ guide: target }} className={RELATED_CLASS}>
      {children}
    </Link>
  );
}

/** A section of real data set ahead of a guide's prose, e.g. the season chart. */
export interface GuideLead {
  title: string;
  node: ReactNode;
}

export function GuidePage({ guide, lead }: { guide: Guide; lead?: GuideLead }) {
  const related = guide.relatedSlugs.flatMap((s) => {
    if (isRelatedPath(s)) return [{ key: s, label: RELATED_PAGES[s] }];
    const g = getGuide(s);
    return g ? [{ key: g.slug, label: g.h1 }] : [];
  });
  const toc: TocItem[] = [
    ...(lead ? [{ id: slugifyHeading(lead.title), label: lead.title }] : []),
    ...guide.sections.map((s) => ({ id: slugifyHeading(s.h), label: s.h })),
    { id: "common-questions", label: "Common questions" },
    { id: "sources", label: "Sources" },
  ];

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Guides", path: "/resources" },
        { name: guide.h1, path: `/${guide.slug}` },
      ]}
      title={guide.h1}
      intro={guide.intro}
    >
      <ReviewedDate iso={guide.lastReviewed} />

      <ArticleBody toc={toc}>
        {lead && (
          <Section id={slugifyHeading(lead.title)} title={lead.title}>
            {lead.node}
          </Section>
        )}
        {guide.sections.map((section) => (
          <Section key={section.h} id={slugifyHeading(section.h)} title={section.h}>
            {section.body && <Prose>{section.body}</Prose>}
            {section.points && <DotList items={section.points} />}
          </Section>
        ))}

        <Section id="common-questions" title="Common questions">
          <Faq items={guide.faq} />
        </Section>

        <div id="sources">
          <SourceList ids={guide.sourceIds} />
        </div>

        {related.length > 0 && (
          <nav aria-labelledby="related-reading">
            <h2 id="related-reading" className="label-caps text-muted-foreground">
              Related reading
            </h2>
            <ul className="mt-2 divide-y divide-border border-y border-border">
              {related.map((r) => (
                <li key={r.key}>
                  <RelatedLink target={r.key}>{r.label}</RelatedLink>
                </li>
              ))}
            </ul>
            <InlineLinks className="mt-4 text-sm">
              <li>
                <Link to="/map" className="link">
                  The map
                </Link>
              </li>
              <li>
                <Link to="/tick-bite-tool" className="link">
                  What to do after a bite
                </Link>
              </li>
              <li>
                <Link to="/prevention" className="link">
                  How to avoid bites
                </Link>
              </li>
            </InlineLinks>
          </nav>
        )}

        <ShareCard
          title={guide.h1}
          subtitle={guide.metaDescription}
          kind="guide"
          summaryText={`${guide.h1}\n\n${guide.intro}\n\nRead more: https://lonestarmap.com/${guide.slug}`}
          sharePath={`/${guide.slug}`}
          shareText={`${guide.h1}, a free guide from LoneStarMap:`}
        />

        <EndNote>
          This guide is here to help you learn. It is not medical advice. If you have symptoms or
          questions, see a doctor. If someone has trouble breathing or a severe allergic reaction,
          call 911. <SourcesAndDisclaimer />
        </EndNote>
      </ArticleBody>
    </PageShell>
  );
}
